import { DeleteOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, message, Modal, Select, Space, Tag } from 'antd';
import * as Component from 'components';
import { seriesSeasonItemService, seriesService } from 'content/service';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { SERVICE_DISTRIBUTION_OPTIONS, STATUS_OPTIONS } from '../../movie/constants';
import { SeriesListItem } from '../../type/seriesType';

const INGEST_SOURCE_OPTIONS = [
  { label: 'In-House', value: 'in-house' },
  { label: 'Mediahold', value: 'mediahold' },
  { label: 'KCP', value: 'kcp' },
];

export default function Series(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [serviceDistributionFilter, setServiceDistributionFilter] = useState<string[]>(
    searchParams.get('service_distribution')?.split(',').filter(Boolean) || []
  );
  const [ingestSourceFilter, setIngestSourceFilter] = useState<string[]>(
    searchParams.get('ingest_source')?.split(',').filter(Boolean) || []
  );
  const [statusFilter, setStatusFilter] = useState<string | null>(searchParams.get('status'));

  const handleServiceDistributionChange = (values: string[]) => {
    setServiceDistributionFilter(values);
    const newParams = new URLSearchParams(searchParams);
    if (values.length > 0) {
      newParams.set('service_distribution', values.join(','));
    } else {
      newParams.delete('service_distribution');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleIngestSourceFilterChange = (values: string[]) => {
    setIngestSourceFilter(values);
    const newParams = new URLSearchParams(searchParams);
    if (values.length > 0) {
      newParams.set('ingest_source', values.join(','));
    } else {
      newParams.delete('ingest_source');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('status', value);
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  // Get English poster thumbnails (landscape and portrait)
  const getEnglishPosters = (posters?: any[]) => {
    if (!posters) return { landscape: null, portrait: null };
    const englishPosters = posters.filter(
      p => p.language?.toUpperCase() === 'EN' || p.language?.toUpperCase() === 'ENGLISH'
    );
    return {
      landscape: englishPosters.find(p => p.orientation === 'landscape'),
      portrait: englishPosters.find(p => p.orientation === 'portrait'),
    };
  };

  // Get latest season for a series
  const getLatestSeason = async (seriesId: number) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('order_by', 'season_order');
      queryParams.append('order_direction', 'desc');
      queryParams.append('page_size', '1');
      const seasons = await seriesSeasonItemService.fetchChildItemsByParentId(
        seriesId,
        queryParams
      );
      return seasons.length > 0 ? seasons[0] : null;
    } catch (error) {
      console.error('Failed to fetch latest season:', error);
      return null;
    }
  };

  const handleCreateEpisode = async (seriesId: number) => {
    try {
      const latestSeason = await getLatestSeason(seriesId);
      if (latestSeason) {
        // Open 'Create Episode' tab pre-filled with latest season metadata
        // Pass both series_id and season_id
        window.open(
          `/episode/edit/new?series_id=${seriesId}&season_id=${latestSeason.id}`,
          '_blank'
        );
      } else {
        message.warning('No season found for this series. Please create a season first.');
      }
    } catch (error) {
      console.error('Failed to create episode:', error);
    }
  };

  const columns = [
    {
      title: 'POSTER',
      dataIndex: 'posters',
      key: 'poster',
      width: '20%',
      render: (_text: string, record: SeriesListItem) => {
        const posters = getEnglishPosters(record.posters);
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {posters.landscape && (
              <Component.ThumbnailComponent
                src={posters.landscape.file}
                width={80}
                height={45}
                alt="landscape"
                emptyText="No poster"
                errorText="Load failed"
                preview
              />
            )}
            {posters.portrait && (
              <Component.ThumbnailComponent
                src={posters.portrait.file}
                width={35}
                height={50}
                alt="portrait"
                emptyText="No poster"
                errorText="Load failed"
                preview
              />
            )}
            {!posters.landscape && !posters.portrait && (
              <div style={{ color: '#999', fontSize: '12px' }}>No poster</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      width: '25%',
      sorter: true,
      render: (_text: string, record: SeriesListItem) => {
        const enTitle = record.i18n_title?.en || record.title_en || '';
        const koTitle = record.i18n_title?.ko || '';
        const zhTitle = record.i18n_title?.['zh-hans'] || '';
        return (
          <div
            onClick={() => navigate(`/series/edit2/${record.id}`)}
            style={{ cursor: 'pointer', color: '#1890ff' }}
          >
            {enTitle && <div style={{ fontWeight: 'bold' }}>{enTitle}</div>}
            {(koTitle || zhTitle) && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {koTitle}
                {koTitle && zhTitle && ' / '}
                {zhTitle}
              </div>
            )}
            {!enTitle && !koTitle && !zhTitle && <div style={{ color: '#999' }}>Untitled</div>}
          </div>
        );
      },
    },
    {
      title: 'SLUG',
      dataIndex: 'slug',
      key: 'slug',
      width: '15%',
      sorter: true,
    },
    // {
    //   title: 'STATUS',
    //   dataIndex: 'status',
    //   key: 'status',
    //   width: '5%',
    //   render: (status: string) => {
    //     const statusMap: Record<string, { color: string; text: string }> = {
    //       published: { color: '#52c41a', text: 'enabled' },
    //       unpublished: { color: '#ff4d4f', text: 'disabled' },
    //     };
    //     const statusInfo = statusMap[status] || { color: '#999', text: status || 'disabled' };
    //     return <span style={{ color: statusInfo.color }}>{statusInfo.text}</span>;
    //   },
    // },
    {
      title: 'INGEST SOURCE',
      dataIndex: 'ingest_source',
      key: 'ingest_source',
      width: '10%',
      render: (ingestSource: string | null | undefined) => {
        if (!ingestSource) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const colorMap: Record<string, string> = {
          mediahold: 'blue',
          kcp: 'purple',
          'in-house': 'green',
        };
        return (
          <Tag color={colorMap[ingestSource.toLowerCase()] || 'default'}>
            {ingestSource.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'ACTIONS',
      dataIndex: 'id',
      key: 'actions',
      width: '10%',
      render: (_: any, record: SeriesListItem) => (
        <Space>
          <Button
            type="text"
            icon={<SettingOutlined />}
            onClick={() => navigate(`/series/edit2/${record.id}`)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              if (record.seasons && record.seasons.length > 0) {
                message.warning(
                  `This series contains ${record.seasons.length} season(s) and cannot be deleted.`
                );
                return;
              }

              Modal.confirm({
                title: 'Are you sure you want to delete?',
                content: 'This action cannot be undone.',
                okText: 'Delete',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: async () => {
                  try {
                    await seriesService.delete(record.id);
                    message.success('Successfully deleted.');
                    window.location.href = '/series/';
                  } catch (error) {
                    console.error('Failed to delete series:', error);
                  }
                },
              });
            }}
          />
          <Button
            type="text"
            icon={<PlusOutlined />}
            style={{ border: '1px solid black' }}
            onClick={() => handleCreateEpisode(record.id)}
          >
            Create Episode
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Component.BasePageComponent>
      <div className="series-page">
        {/* Filter Section */}
        <div
          style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
          }}
        >
          <Space wrap size="middle">
            <div>
              <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                Service Distribution
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="All Sources"
                value={serviceDistributionFilter}
                onChange={handleServiceDistributionChange}
                options={SERVICE_DISTRIBUTION_OPTIONS}
              />
            </div>
            <div>
              <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                Ingest Source
              </div>
              <Select
                mode="multiple"
                style={{ width: 200 }}
                placeholder="All Sources"
                value={ingestSourceFilter}
                onChange={handleIngestSourceFilterChange}
                options={INGEST_SOURCE_OPTIONS}
              />
            </div>
            {/* <div>
              <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Status</div>
              <Select
                style={{ width: 150 }}
                placeholder="All Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                options={STATUS_OPTIONS}
              />
            </div> */}
          </Space>
        </div>

        {/* Table */}
        <Component.TableComponent<SeriesListItem>
          name="Series"
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          additionalButton={[
            <Button
              key="add-distribution"
              icon={<PlusOutlined />}
              onClick={() => {
                // TODO: Implement Add Distribution feature
                message.info('Add Distribution feature will be implemented later.');
              }}
            >
              ADD DISTRIBUTION
            </Button>,
            <Button
              key="create-series"
              icon={<PlusOutlined />}
              onClick={() => navigate('/series/edit2/new')}
            >
              ADD SERIES
            </Button>,
            <Button
              key="bulk-update"
              onClick={() => {
                console.log('Bulk update');
              }}
            >
              BULK UPDATE
            </Button>,
          ]}
          columns={columns}
          dataFetch={async (params: URLSearchParams) => {
            try {
              const result = await seriesService.fetchSeriesList(params);

              // SeriesListSerializer already includes posters, no separate call needed
              return {
                data: result.results,
                count: result.count,
              };
            } catch (error) {
              console.error('Failed to fetch series list:', error);
              // Fallback: use existing fetch
              const result = await seriesService.fetch(params);
              return {
                data: result.results.map((s: any) => ({
                  id: s.id,
                  slug: s.slug,
                  status: s.status,
                  title: s.title,
                  title_en: s.title_en,
                  posters: s.posters || [],
                  seasons: s.seasons,
                })) as SeriesListItem[],
                count: result.count,
              };
            }
          }}
        />
      </div>
    </Component.BasePageComponent>
  );
}
